/**
 * sales.ts — 영업(ebiz영업_중앙 미러) 도메인 타입 + 매퍼.
 *
 * 컬렉션(전부 SQL→Firebase 단방향, 영구 read-only):
 *   sales_invoices  매출전표 (ksms310)   — 본 화면의 주 데이터
 *   sales_customers 거래처 마스터 (ksms110)
 *   sales_sites     현장 마스터 (ksms165) — doc id = {customerCode}_{siteCode}
 *   sales_reps      담당자 마스터 (ksms150)
 *
 * ⚠️ 데이터 사실 (메모리):
 *   - date 는 Firestore Timestamp 이며 "UTC 자정 = 거래 달력일"로 저장됨.
 *     → toDate().toISOString().slice(0,10) 이 곧 거래일(YYYY-MM-DD).
 *   - total_amount 필드는 ERP가 안 채워 전부 0 → 합계는 amount + vat 로 계산.
 *   - 관급(gov)/사급(cust)은 C023 화이트리스트. 전표엔 tier 가 이미 부여돼 있고,
 *     마스터(거래처)에는 tier 가 없어 코드로 분류한다.
 */
import { Timestamp, type DocumentData, type QueryDocumentSnapshot } from "firebase/firestore";

export type Tier = "gov" | "cust";

/** 관급으로 분류되는 거래처 코드 (1법인 = C023 단일) */
export const GOV_CUSTOMER_CODES = new Set<string>(["C023"]);

export function classifyTier(customerCode: string): Tier {
  return GOV_CUSTOMER_CODES.has(customerCode.trim()) ? "gov" : "cust";
}

export interface SalesInvoice {
  id: string;
  date: string;        // YYYY-MM-DD (거래 달력일)
  tier: Tier;
  customerCode: string;
  customerName: string;
  siteCode: string;
  siteName: string;
  repCode: string;
  repName: string;
  mixSpec: string;     // 규격 (예: 25-21-150)
  unitPrice: number;   // 단가 (원/㎥)
  qtyM3: number;       // 수량 (㎥)
  amount: number;      // 공급가액
  vat: number;         // 부가세
  total: number;       // 합계금액 = amount + vat
}

/** 수금전표 (ksms320). 거래처·현장 단위 수금액(= total). 규격/수량/부가세 없음. */
export interface SalesReceipt {
  id: string;
  date: string;        // 수금일 YYYY-MM-DD
  tier: Tier;          // 거래처코드로 분류 (전표에 tier 없음)
  customerCode: string;
  customerName: string;
  siteCode: string;
  siteName: string;
  repCode: string;
  repName: string;
  amount: number;      // 수금액 (total 우선, 없으면 amount)
  receiptType: string; // 수금구분 (대부분 빈값)
  noteNo: string;      // 어음번호 (있으면 어음 수금)
  noteDueDate: string; // 어음만기일
}

export interface SalesRep {
  code: string;
  name: string;
  position?: string;
  department?: string;
}

export interface SalesCustomer {
  code: string;
  name: string;
  tier: Tier;
  repCode: string;
  repName: string;
}

export interface SalesSite {
  id: string;          // {customerCode}_{siteCode}
  siteCode: string;
  siteName: string;
  customerCode: string;
  repCode: string;
  repName: string;
}

/**
 * 거래처 상세(ERP 부가 컬럼) — sales_customers 문서의 ksms110 미러 필드.
 * 목록용 SalesCustomer 는 가볍게 유지하고, 상세는 선택 시 단건 getDoc 으로 읽는다.
 */
export interface SalesCustomerDetail {
  tel: string;
  mobile: string;
  fax: string;
  email: string;
  email2: string;
  address: string;
  postalCode: string;
  contactName: string;       // 거래처담당자(상대측) 이름
  contactPosition: string;   // 거래처담당자 직급
  paymentTerms: string;      // 결제조건
  collectionDue: string;     // 수금기일
  creditLimit: number;       // 여신한도
  vatType: string;           // 부가세구분
  businessNo: string;        // 사업자번호
  corpName: string;          // 법인명
  businessType: string;      // 업태
  businessItem: string;      // 종목
}

/** 거래처 미수 스냅샷 (ERP 미수현황 누계미수). sales_balances/{customer_code} */
export interface SalesBalanceSite {
  name: string;
  ar: number;          // 누계미수 (현장)
  carryover: number;   // 전월이월
  last_sale: string;   // 최종매출일 YYYY-MM-DD
}
export interface SalesBalance {
  customerCode: string;
  customerName: string;
  repName: string;
  ar: number;          // 미수 (롤포워드 있으면 그 값, 없으면 엑셀 기준값)
  carryover: number;
  asOf: string;        // 기준일 YYYY-MM-DD (롤포워드면 동기화일)
  rolled: boolean;     // 동기화 롤포워드 적용 여부 ("자동" 표기용)
  sites: Record<string, SalesBalanceSite>;
}

/** 관급 현장별 잔량 (배정−출하). sales_allocations/{customer_code} */
export interface SalesAllocationSite {
  name: string;
  allocated: number;   // 배정량
  shipped: number;     // 출하량
  remain: number;      // 잔량 = 배정 − 출하
  allocDate: string;   // 배정일
  dueDate: string;     // 마감일(납기)
  lastPour: string;    // 최근타설일
}
export interface SalesAllocation {
  customerCode: string;
  customerName: string;
  asOf: string;
  totalAllocated: number;
  totalShipped: number;
  totalRemain: number;
  sites: Record<string, SalesAllocationSite>;
}

// ── 변환 헬퍼 ──────────────────────────────────────────────────────────

function str(v: unknown, def = ""): string {
  return typeof v === "string" ? v : def;
}
function num(v: unknown, def = 0): number {
  const n = Number(v ?? def);
  return Number.isFinite(n) ? n : def;
}
/** Firestore Timestamp(UTC 자정) → 거래일 문자열 YYYY-MM-DD */
function dateStr(v: unknown): string {
  if (v instanceof Timestamp) return v.toDate().toISOString().slice(0, 10);
  return "";
}

export function invoiceFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesInvoice {
  const d = snap.data();
  const amount = num(d.amount);
  const vat = num(d.vat);
  const tier: Tier = d.tier === "gov" ? "gov" : d.tier === "cust" ? "cust" : classifyTier(str(d.customer_code));
  return {
    id: snap.id,
    date: dateStr(d.date),
    tier,
    customerCode: str(d.customer_code),
    customerName: str(d.customer_name),
    siteCode: str(d.site_code),
    siteName: str(d.site_name),
    repCode: str(d.rep_code),
    repName: str(d.rep_name),
    mixSpec: str(d.mix_spec),
    unitPrice: num(d.unit_price),
    qtyM3: num(d.qty_m3),
    amount,
    vat,
    total: amount + vat, // total_amount 신뢰 불가 → 직접 계산
  };
}

export function receiptFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesReceipt {
  const d = snap.data();
  return {
    id: snap.id,
    date: dateStr(d.date),
    tier: classifyTier(str(d.customer_code)),
    customerCode: str(d.customer_code),
    customerName: str(d.customer_name),
    siteCode: str(d.site_code),
    siteName: str(d.site_name),
    repCode: str(d.rep_code),
    repName: str(d.rep_name),
    amount: num(d.total) || num(d.amount), // 수금액 = total (vat 0). total 0이면 amount fallback
    receiptType: str(d.receipt_type),
    noteNo: str(d.note_no),
    noteDueDate: dateStr(d.note_due_date),
  };
}

export function repFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesRep {
  const d = snap.data();
  return {
    code: str(d.rep_code, snap.id),
    name: str(d.rep_name),
    position: d.position ? str(d.position) : undefined,
    department: d.department ? str(d.department) : undefined,
  };
}

export function customerFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesCustomer {
  const d = snap.data();
  const code = str(d.customer_code, snap.id);
  return {
    code,
    name: str(d.customer_name),
    tier: classifyTier(code),
    repCode: str(d.rep_code),
    repName: str(d.rep_name),
  };
}

/** 거래처 상세(ERP 부가 컬럼) 매퍼 — 단건 문서 data() 에서 추출 */
export function customerDetailFromData(d: DocumentData): SalesCustomerDetail {
  return {
    tel: str(d.tel),
    mobile: str(d.mobile),
    fax: str(d.fax),
    email: str(d.email),
    email2: str(d.email2),
    address: str(d.address),
    postalCode: str(d.postal_code),
    contactName: str(d.customer_contact_name),
    contactPosition: str(d.customer_contact_position),
    paymentTerms: str(d.payment_terms),
    collectionDue: str(d.collection_due),
    creditLimit: num(d.credit_limit),
    vatType: str(d.vat_type),
    businessNo: str(d.business_no),
    corpName: str(d.corp_name),
    businessType: str(d.business_type),
    businessItem: str(d.business_item),
  };
}

export function siteFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesSite {
  const d = snap.data();
  return {
    id: snap.id,
    siteCode: str(d.site_code),
    siteName: str(d.site_name),
    customerCode: str(d.customer_code),
    repCode: str(d.rep_code),
    repName: str(d.rep_name),
  };
}

export function balanceFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesBalance {
  const d = snap.data();
  const sites: Record<string, SalesBalanceSite> = {};
  const raw = (d.sites && typeof d.sites === "object") ? d.sites : {};
  for (const [k, v] of Object.entries(raw)) {
    const s = (v ?? {}) as DocumentData;
    sites[k] = { name: str(s.name), ar: num(s.ar), carryover: num(s.carryover), last_sale: str(s.last_sale) };
  }
  // 롤포워드(now) 적용 — 있으면 ar/asOf/site.ar 를 추정 최신값으로 덮음 (기준값은 보존 안 해도 표시엔 불필요)
  const now = (d.now && typeof d.now === "object") ? d.now as DocumentData : null;
  const rolled = !!now;
  if (now) {
    const nowSites = (now.sites && typeof now.sites === "object") ? now.sites as Record<string, unknown> : {};
    for (const [k, v] of Object.entries(nowSites)) {
      if (sites[k]) sites[k] = { ...sites[k], ar: num(v) };
      else sites[k] = { name: "", ar: num(v), carryover: 0, last_sale: "" };
    }
  }
  return {
    customerCode: str(d.customer_code, snap.id),
    customerName: str(d.customer_name),
    repName: str(d.rep_name),
    ar: now ? num(now.ar) : num(d.ar),
    carryover: num(d.carryover),
    asOf: now ? str(now.as_of) : str(d.as_of),
    rolled,
    sites,
  };
}

export function allocationFromFirestore(snap: QueryDocumentSnapshot<DocumentData>): SalesAllocation {
  const d = snap.data();
  const sites: Record<string, SalesAllocationSite> = {};
  const raw = (d.sites && typeof d.sites === "object") ? d.sites : {};
  for (const [k, v] of Object.entries(raw)) {
    const s = (v ?? {}) as DocumentData;
    sites[k] = {
      name: str(s.name),
      allocated: num(s.allocated),
      shipped: num(s.shipped),
      remain: num(s.remain),
      allocDate: str(s.alloc_date),
      dueDate: str(s.due_date),
      lastPour: str(s.last_pour),
    };
  }
  return {
    customerCode: str(d.customer_code, snap.id),
    customerName: str(d.customer_name),
    asOf: str(d.as_of),
    totalAllocated: num(d.total_allocated),
    totalShipped: num(d.total_shipped),
    totalRemain: num(d.total_remain),
    sites,
  };
}
